import { useState, useEffect, useCallback, useMemo } from "react";
import {
  listUsers,
  listUserPermissions,
  updateUserStatus,
  updateUserPermissions,
  AVAILABLE_PERMISSIONS,
} from "../../api/endpoints/users.js";
import { filterBySearch } from "../services/entityFilters.js";

export function useUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [addPermOpen, setAddPermOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const users = await listUsers();
      setItems(users);
      setSelectedId((prev) => {
        if (prev && users.some((u) => u.id === prev)) return prev;
        return users[0]?.id ?? null;
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = filterBySearch(items, search, ["name", "email", "status"]);
  const selectedUser = useMemo(
    () => items.find((u) => u.id === selectedId) ?? null,
    [items, selectedId],
  );

  useEffect(() => {
    if (!selectedId) {
      setPermissions([]);
      return;
    }
    let cancelled = false;
    setPermsLoading(true);
    listUserPermissions(selectedId)
      .then((perms) => {
        if (!cancelled) setPermissions(perms);
      })
      .finally(() => {
        if (!cancelled) setPermsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const removePermission = useCallback(async (perm) => {
    if (!selectedId) return;
    const next = permissions.filter((p) => p !== perm);
    setPermissions(next);
    await updateUserPermissions(selectedId, next);
  }, [selectedId, permissions]);

  const addPermission = useCallback(async (perm) => {
    if (!selectedId || permissions.includes(perm)) return;
    const next = [...permissions, perm];
    setPermissions(next);
    await updateUserPermissions(selectedId, next);
    setAddPermOpen(false);
  }, [selectedId, permissions]);

  const disableUser = useCallback(async () => {
    if (!selectedUser || selectedUser.status !== "active") return;
    setStatusSaving(true);
    try {
      const updated = await updateUserStatus(selectedUser.id, "inactive");
      setItems((list) => list.map((u) => (u.id === updated.id ? updated : u)));
    } finally {
      setStatusSaving(false);
    }
  }, [selectedUser]);

  const enableUser = useCallback(async () => {
    if (!selectedUser || selectedUser.status === "active") return;
    setStatusSaving(true);
    try {
      const updated = await updateUserStatus(selectedUser.id, "active");
      setItems((list) => list.map((u) => (u.id === updated.id ? updated : u)));
    } finally {
      setStatusSaving(false);
    }
  }, [selectedUser]);

  const addablePermissions = useMemo(
    () => AVAILABLE_PERMISSIONS.filter((p) => !permissions.includes(p)),
    [permissions],
  );

  return {
    items: visible,
    allItems: items,
    loading,
    error,
    search,
    setSearch,
    reload,
    total: items.length,
    selectedId,
    setSelectedId,
    selectedUser,
    permissions,
    permsLoading,
    removePermission,
    addPermission,
    addablePermissions,
    addPermOpen,
    setAddPermOpen,
    disableUser,
    enableUser,
    statusSaving,
  };
}
